import type { RouteName } from "routing";

import { request } from "@frontend/lib/http";

import type { Reference, ReferenceListName } from "./types";

const ROUTES: Record<ReferenceListName, RouteName> = {
  accountantTypes: "api_projects_accountant_types",
  affiliates: "api_projects_affiliates",
  billings: "api_projects_billings",
  clientAccountantTypes: "api_client_accountant_types",
  clientContactTypes: "api_client_contact_type_references",
  clientFields: "api_client_fields",
  clientRegions: "api_client_regions",
  clientStatuses: "api_client_statuses",
  contactTitles: "api_client_titles",
  employeeAccountants: "api_employee_accountants",
  employeeCategories: "api_employee_categories_list",
  employeeTypes: "api_employee_types_list",
  expenseRecurrences: "api_projects_expense_recurrences",
  holidayActions: "api_projects_holiday_actions",
  lagDayTypes: "api_projects_lag_day_types",
  lagTimeTypes: "api_projects_lag_time_types",
  paymentPeriods: "api_client_payment_periods",
  paymentTargets: "api_client_payment_targets",
  projectAccountants: "api_projects_accountants",
  projectStatuses: "api_projects_statuses",
  riskClasses: "api_client_risk_classes",
  serviceGroups: "api_projects_service_groups",
  sites: "api_sites_list",
  subscriptionBillingTimes: "api_client_subscription_billing_times",
  subTaskTypes: "api_projects_sub_task_types",
  taskTypes: "api_projects_task_types",
  unitTypes: "api_projects_unit_types",
};

export type ReferenceFilter = {
  serviceGroupId?: number;
  taskTypeId?: number;
  closureWeeksYear?: number;
};

const cache = new Map<string, Promise<Reference[]>>();

export function loadReferences(
  list: ReferenceListName,
  filter: ReferenceFilter = {},
): Promise<Reference[]> {
  const key = cacheKey(list, filter);
  const pending = cache.get(key);

  if (pending) {
    return pending;
  }

  const promise = request<Reference[]>({
    route: ROUTES[list],
    query: {
      serviceGroupId: filter.serviceGroupId,
      taskTypeId: filter.taskTypeId,
      closureWeeksYear: filter.closureWeeksYear,
    },
  }).catch((error: unknown) => {
    cache.delete(key);

    throw error;
  });

  cache.set(key, promise);

  return promise;
}

export function clearReferences(list?: ReferenceListName): void {
  if (undefined === list) {
    cache.clear();

    return;
  }

  for (const key of cache.keys()) {
    if (key === list || key.startsWith(`${list}:`)) {
      cache.delete(key);
    }
  }
}

function cacheKey(list: ReferenceListName, filter: ReferenceFilter): string {
  const scope = [
    filter.serviceGroupId,
    filter.taskTypeId,
    filter.closureWeeksYear,
  ]
    .filter((value) => undefined !== value)
    .join(":");

  return "" === scope ? list : `${list}:${scope}`;
}
